// SuperAdmin Tenant Management — list view
// Route: /platform/tenants  (admin role only)
//
// Shows every TenantConfig as a compact card in a responsive grid. Click a
// card to open the detail/edit view. The "+ New Tenant Config" button opens
// an inline modal that pulls candidate orgs from /platform/tenants/discover.

import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
axios.defaults.withCredentials = true;

const C = {
  burgundy:    '#800020',
  gold:        '#C4A265',
  textPrimary: '#1e293b',
  textMuted:   '#64748b',
  bgCard:      '#f8fafc',
  border:      '#e2e8f0',
  green:       '#10b981',
  gray:        '#94a3b8',
};

// Feature presets matching the prompt — also reused in the detail screen.
export const FEATURE_PRESETS = {
  mgaFullSuite: {
    label: 'MGA Full Suite',
    description: 'Everything except communications. ARGOS-style.',
    features: {
      dashboard: true, documentProcessing: true, workflowAutomation: true,
      claimsAnalytics: true, riskSignals: true,
      clients: true, claims: true, policies: true, offers: true,
      invoicing: true, commissions: true,
      rateTables: true, pgma: true, rcPro: true, pgm: true,
      policyComparison: true,
      chat: false, video: false, eSign: false,
      auditTrail: true, integrityMonitor: true, compliance: true,
      dsar: true, aiGovernance: true,
      teamManagement: true, integrations: false,
      brokers: true, riskCarriers: true, bordereaux: true,
      apiKeys: false, webhooks: false, dataResidency: true,
    },
  },
  agencyCompareReview: {
    label: 'Agency — Compare & Review',
    description: 'Dashboard, Clients, Policies, Document AI. Minnesota-style.',
    features: {
      dashboard: true, documentProcessing: true,
      clients: true, policies: true, policyComparison: true,
      auditTrail: true, teamManagement: true,
      workflowAutomation: false, claimsAnalytics: false, riskSignals: false,
      claims: false, offers: false, invoicing: false, commissions: false,
      rateTables: false, pgma: false, rcPro: false, pgm: false,
      chat: false, video: false, eSign: false,
      integrityMonitor: false, compliance: false, dsar: false, aiGovernance: false,
      integrations: false,
      brokers: false, riskCarriers: false, bordereaux: false,
      apiKeys: false, webhooks: false, dataResidency: false,
    },
  },
  minimal: {
    label: 'Minimal',
    description: 'Dashboard + team management only.',
    features: {
      dashboard: true, teamManagement: true,
      documentProcessing: false, workflowAutomation: false,
      claimsAnalytics: false, riskSignals: false,
      clients: false, claims: false, policies: false, offers: false,
      invoicing: false, commissions: false,
      rateTables: false, pgma: false, rcPro: false, pgm: false,
      policyComparison: false,
      chat: false, video: false, eSign: false,
      auditTrail: true, integrityMonitor: false, compliance: false,
      dsar: false, aiGovernance: false,
      integrations: false,
      brokers: false, riskCarriers: false, bordereaux: false,
      apiKeys: false, webhooks: false, dataResidency: false,
    },
  },
};

export const LOCALE_PRESETS = {
  US: {
    language: 'en', country: 'US', currency: 'USD', currencySymbol: '$',
    dateFormat: 'MM/DD/YYYY', timezone: 'America/New_York',
    numberFormat: { thousandsSeparator: ',', decimalSeparator: '.' },
    stateLabel: 'State', postalCodeLabel: 'ZIP Code', phoneFormat: 'US',
  },
  CH: {
    language: 'fr', country: 'CH', currency: 'CHF', currencySymbol: 'CHF',
    dateFormat: 'DD.MM.YYYY', timezone: 'Europe/Zurich',
    numberFormat: { thousandsSeparator: "'", decimalSeparator: '.' },
    stateLabel: 'Canton', postalCodeLabel: 'NPA', phoneFormat: 'CH',
  },
};

function countEnabled(features) {
  if (!features) return { enabled: 0, total: 0 };
  const entries = Object.entries(features);
  return {
    enabled: entries.filter(([, v]) => v === true).length,
    total:   entries.length,
  };
}

function Badge({ active }) {
  const { t } = useTranslation();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: active ? 'rgba(16,185,129,0.12)' : 'rgba(148,163,184,0.15)',
      color:      active ? C.green : C.textMuted,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: active ? C.green : C.gray }} />
      {active ? t('screens.platformTenants.active') : t('screens.platformTenants.inactive')}
    </span>
  );
}

const PlatformTenants = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userLogin = useSelector(state => state.userLogin);
  const user = userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data;

  // Access control — admin (SaaS-god) only. systemAdmin is a tenant admin
  // and must NOT have access to cross-tenant config.
  useEffect(() => {
    if (user && user.role !== 'admin') {
      history.push('/dashboard');
    }
  }, [user, history]);

  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [showNew,  setShowNew]  = useState(false);
  const [opsResult, setOpsResult] = useState(null); // { title, kind: 'ok'|'err', body: <ReactNode> }
  const [opsBusy,   setOpsBusy]   = useState(null); // 'migrate' | 'seed' | 'reset' | null
  const [copyOk,    setCopyOk]    = useState(false); // ephemeral "Copied ✓" flag for the password panel

  // Deployment scope gates the cluster-specific ops buttons. On regional
  // deploys (regional-eu / regional-us), Seed SaaS God, Reset SaaS God
  // Password, and the pre-filled Seed X Demo buttons don't make sense —
  // some 404 at the API, one (Seed X Demo) succeeds against the wrong
  // cluster. Hide until we confirm we're on platform-admin.
  const [deployScope, setDeployScope] = useState(null); // 'platform-admin' | 'regional-eu' | ...
  useEffect(() => {
    // /health is unauthenticated and sits above /api/v1 — derive host from BASE_URL
    const healthUrl = BASE_URL.replace(/\/api\/v1\/?$/, '') + '/health';
    axios.get(healthUrl).then(({ data }) => {
      setDeployScope(String(data?.deployment?.scope || 'platform-admin').toLowerCase());
    }).catch(() => setDeployScope('platform-admin'));
  }, []);
  const isPlatformAdminDeploy = deployScope === 'platform-admin';

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/platform/tenants`);
      setTenants(data?.data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const runMigrateKeys = async () => {
    const dryRun = window.confirm(
      'Migrate TenantConfig org keys.\n\n' +
      'OK   = DRY RUN (report only, no writes)\n' +
      'Cancel = APPLY (commits changes to the database)\n\n' +
      'Run DRY RUN first?'
    );
    // Second confirm on apply so a mis-click can't destroy anything.
    if (!dryRun) {
      if (!window.confirm('APPLY — this will re-key TenantConfig documents and may delete duplicates. Continue?')) return;
    }
    setOpsBusy('migrate');
    setOpsResult(null);
    try {
      const url = `${BASE_URL}/platform/tenants/migrate-keys${dryRun ? '?dryRun=true' : ''}`;
      const { data } = await axios.post(url, {});
      setOpsResult({
        title: `Migration ${dryRun ? 'dry-run' : 'applied'} — ${data.summary.total} configs scanned`,
        kind: 'ok',
        body: (
          <div style={{ fontSize: 12, color: C.textPrimary }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontWeight: 600 }}>
              <span>rekeyed: {data.summary.fixed}</span>
              <span>kept: {data.summary.kept}</span>
              <span>duplicates deleted: {data.summary.deleted}</span>
              <span>orphaned: {data.summary.orphaned}</span>
            </div>
            {data.details?.length > 0 && (
              <details>
                <summary style={{ cursor: 'pointer', color: C.burgundy, fontWeight: 600 }}>Per-doc log ({data.details.length})</summary>
                <ul style={{ maxHeight: 220, overflowY: 'auto', margin: '8px 0 0', paddingLeft: 20, fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6 }}>
                  {data.details.map((d, i) => (
                    <li key={i}>
                      <b>{d.action}</b> {d.orgName || '(no name)'} — {d.currentOrg}
                      {d.correctOrg ? ` → ${d.correctOrg}` : ''}
                      {d.collisionOrgName ? ` (collides with ${d.collisionOrgName})` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {!dryRun && data.summary.fixed + data.summary.deleted > 0 && (
              <div style={{ marginTop: 10, padding: 8, background: 'rgba(196,162,101,0.14)', borderRadius: 6, color: '#78350f' }}>
                Tenant config cache was cleared server-side. Users will see updated features on their next request.
              </div>
            )}
          </div>
        ),
      });
      if (!dryRun) loadTenants();
    } catch (err) {
      setOpsResult({ title: t('screens.platformTenants.migrationFailed'), kind: 'err', body: <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div> });
    } finally {
      setOpsBusy(null);
    }
  };

  const runSeedSaasGod = async () => {
    if (!window.confirm(
      'Seed / repair the SaaS God account (admin@getregure.com).\n\n' +
      'This is idempotent — safe to run multiple times. It will:\n' +
      '  • Create the account if missing, OR reset role/active/password on an existing one.\n' +
      '  • Create or update the TenantConfig with all features enabled.\n' +
      '  • Reset the password to the SEED_SAAS_GOD_PASSWORD env var (or placeholder).\n\n' +
      'Continue?'
    )) return;
    setOpsBusy('seed');
    setOpsResult(null);
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/seed-admin`, {});
      setOpsResult({
        title: `SaaS God account ready — ${data.account.email}`,
        kind: 'ok',
        body: (
          <div style={{ fontSize: 12, color: C.textPrimary }}>
            <div style={{ marginBottom: 8 }}>
              <b>{data.account.name}</b> · role={data.account.role} · active={String(data.account.active)}
            </div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {data.actions.map((a, i) => (
                <li key={i}><b>{a.action}</b> {a.target}{a.fieldsSet ? ` (${a.fieldsSet.join(', ')})` : ''}</li>
              ))}
            </ul>
            <div style={{ marginTop: 10, padding: 8, background: data.password.usingDefaultPlaceholder ? '#fef3c7' : '#ecfdf5', borderRadius: 6, color: data.password.usingDefaultPlaceholder ? '#92400e' : '#065f46' }}>
              {data.password.hint}
            </div>
          </div>
        ),
      });
      loadTenants();
    } catch (err) {
      setOpsResult({ title: t('screens.platformTenants.seedFailed'), kind: 'err', body: <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div> });
    } finally {
      setOpsBusy(null);
    }
  };

  const runSeedMinnesota = async () => {
    if (!window.confirm(
      'Seed the Minnesota River Insurance trial tenant on the DEFAULT (shared) cluster.\n\n' +
      'WARNING — clients/policies/documents/comparisons are NOT idempotent. Only click if Minnesota has not been seeded yet, or you want to add another copy of the seed data. Users ARE idempotent.\n\n' +
      'What it does:\n' +
      '  • Create Amber Weber (aweber@agquest.net) as systemAdmin plus two support users, all scoped to the Minnesota tenant.\n' +
      '  • Create 50 clients, ~120 policies, ~25 documents, and 5 policy comparisons.\n' +
      '  • Write the tenant config to the default cluster (mongoConnectionName: default).\n' +
      '  • S3 uploads land under the Minnesota org prefix on the shared bucket.\n\n' +
      'The seed runs asynchronously (~30-60 seconds); this panel polls for completion.\n\n' +
      'Continue?'
    )) return;
    setOpsBusy('mn-seed');
    setOpsResult({
      title: 'Minnesota seed queued — starting…',
      kind: 'ok',
      body: <div style={{ fontSize: 12 }}>Polling for completion. Typical: 30-60 seconds. Please do not reload the page.</div>,
    });

    // Kick off the seed. Returns 202 with a jobId; the seed runs in the
    // background so the reverse-proxy timeout can't kill it.
    let jobId;
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/seed-minnesota-river`, {});
      jobId = data.jobId;
    } catch (err) {
      setOpsResult({
        title: 'Minnesota seed failed to start',
        kind: 'err',
        body: <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div>,
      });
      setOpsBusy(null);
      return;
    }

    const pollUrl = `${BASE_URL}/platform/seed-minnesota-river/status/${jobId}`;
    const start = Date.now();
    const MAX_MS = 5 * 60 * 1000; // 5-minute hard cap
    let job = null;
    while (Date.now() - start < MAX_MS) {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const { data } = await axios.get(pollUrl);
        job = data.job;
        if (job.state === 'completed' || job.state === 'failed') break;
        const elapsed = Math.floor((Date.now() - job.startedAt) / 1000);
        setOpsResult({
          title: `Minnesota seed running (${elapsed}s elapsed)…`,
          kind: 'ok',
          body: <div style={{ fontSize: 12 }}>Polling for completion. Typical: 30-60s.</div>,
        });
      } catch (_) { /* transient poll failure — keep trying */ }
    }

    if (!job || job.state === 'running') {
      setOpsResult({
        title: 'Minnesota seed timed out on client side',
        kind: 'err',
        body: <div style={{ fontSize: 12 }}>The seed may still be running server-side. Refresh in 30 seconds and check Team Management for Amber Weber to confirm.</div>,
      });
      setOpsBusy(null);
      return;
    }

    if (job.state === 'failed') {
      setOpsResult({
        title: 'Minnesota seed failed',
        kind: 'err',
        body: <div style={{ fontSize: 12 }}>{job.error || 'Unknown error'}</div>,
      });
      setOpsBusy(null);
      return;
    }

    setOpsResult({
      title: `${job.tenant.orgName} seeded — org ${String(job.tenant.orgId).slice(-8)}`,
      kind: 'ok',
      body: (
        <div style={{ fontSize: 12, color: C.textPrimary }}>
          <div style={{ marginBottom: 8 }}>
            strategy: <b>{job.tenant.strategy}</b> · cluster: <b>{job.tenant.cluster}</b> · elapsed: <b>{job.elapsedSec}s</b>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontWeight: 600 }}>
            <span>users: {job.counts.users}</span>
            <span>clients: {job.counts.clients}</span>
            <span>policies: {job.counts.policies}</span>
            <span>docs: {job.counts.documents}</span>
            <span>comparisons: {job.counts.comparisons}</span>
          </div>
          <div style={{ padding: 8, background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 6, color: '#065f46', marginBottom: 8 }}>
            Login: <b>{job.login.email}</b> / <code style={{ fontFamily: 'ui-monospace, monospace' }}>{job.login.password}</code>
          </div>
          {job.s3?.bucket && (
            <div style={{ fontSize: 11, color: C.textMuted }}>
              S3 uploads landed under <code>{job.s3.prefix}</code> on bucket <code>{job.s3.bucket}</code>.
            </div>
          )}
          <div style={{ marginTop: 10, padding: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, color: '#78350f', fontSize: 11 }}>
            {job.routingNote}
          </div>
        </div>
      ),
    });
    loadTenants();
    setOpsBusy(null);
  };

  // ─── Shared demo-tenant seed runner ─────────────────────────
  // Consumes the response shape from /platform/tenants/seed-demo-tenant
  // and renders the standard result panel: credentials block + sample
  // deal + seeded FX rates (when present) + safety note. Every "Seed X
  // Demo" button funnels through this, which keeps their panels in sync.
  const seedDemoTenant = async ({ payload, busyKey, failTitle }) => {
    setOpsBusy(busyKey);
    setOpsResult(null);
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/tenants/seed-demo-tenant`, payload);
      setOpsResult({
        title: `${data.tenant.orgName} seeded — org ${data.tenant.orgId.slice(-8)} · cluster ${data.tenant.cluster}`,
        kind: 'ok',
        body: (
          <div style={{ fontSize: 12, color: C.textPrimary }}>
            <div style={{ marginBottom: 10, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontWeight: 600 }}>
              ⚠ Passwords will not be shown again. Copy them now.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {data.users.map((u) => (
                <div key={u.email} style={{
                  display: 'grid', gridTemplateColumns: '110px 220px 1fr', gap: 8,
                  padding: '8px 10px', background: '#0f172a', color: '#f8fafc',
                  borderRadius: 6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
                }}>
                  <span style={{ color: '#94a3b8' }}>{u.role}</span>
                  <span style={{ userSelect: 'all' }}>{u.email}</span>
                  <span style={{ userSelect: 'all', letterSpacing: 0.4 }}>{u.plaintextPassword}</span>
                </div>
              ))}
            </div>
            {data.sampleDeal && (
              <div style={{ padding: 8, background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 6, color: '#065f46', marginBottom: 8 }}>
                Sample deal created: <b>{data.sampleDeal.dealNumber}</b> — {data.sampleDeal.cedantName} · {data.sampleDeal.currency} {Number(data.sampleDeal.premium).toLocaleString()}
                {data.sampleDeal.marketCount > 0 && <> · {data.sampleDeal.marketCount} market row{data.sampleDeal.marketCount === 1 ? '' : 's'}</>}
              </div>
            )}
            {Array.isArray(data.fxRates) && data.fxRates.length > 0 && (
              <div style={{ padding: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e40af', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>FX rates seeded ({data.fxRates.length})</div>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                  {data.fxRates.map((r, i) => (
                    <div key={i}>1 {r.base} = {r.rate} {r.quote}</div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ padding: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e40af', fontSize: 11 }}>
              {data.safetyNote}
            </div>
          </div>
        ),
      });
      loadTenants();
    } catch (err) {
      setOpsResult({
        title: failTitle,
        kind: 'err',
        body: <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div>,
      });
    } finally {
      setOpsBusy(null);
    }
  };

  // ─── Backfill Reinsurance Flags ────────────────────────────────
  // Purely additive migration for tenants seeded before newer reinsurance
  // sub-flags existed. Test Run 7-22 R5.2/R5.3: Atrea Demo hit
  // "feature not enabled" for endorsements because its TenantConfig
  // predated Module B and had no `reinsuranceEndorsements` key at all.
  //
  // Test Run 7-23 R7: an earlier version of this button also sent
  // enableOn={endorsements,premiumDepth,sanctions}=true. That flipped
  // Atrea's sanctions and depth flags from false → true because the
  // backend's enableOn branch overrides "present but false". Atrea's
  // seed intentionally leaves depth+sanctions OFF (it's the "simple
  // tenant" acceptance case). The button no longer sends enableOn — it
  // only fills MISSING keys with false. To enable a specific flag on a
  // tenant, use the per-tenant edit UI.
  const runBackfillReinsuranceFlags = async () => {
    if (!window.confirm(
      'Backfill missing reinsurance feature flags on ALL tenants.\n\n' +
      'Purely additive — only fills missing keys with false.\n' +
      'Never modifies an existing flag value.\n' +
      'To enable a specific flag on a tenant, use the per-tenant edit UI.\n\n' +
      'Idempotent — safe to run multiple times.\n\n' +
      'Continue?'
    )) return;
    setOpsBusy('backfill');
    setOpsResult(null);
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/tenants/backfill-reinsurance-flags`, {});
      setOpsResult({
        title: `Backfill complete — patched ${data.patchedCount} of ${data.totalScanned} tenant(s)`,
        kind: 'ok',
        body: (
          <div style={{ fontSize: 12, color: C.textPrimary }}>
            {data.patched.length === 0 ? (
              <div>Nothing to patch — every reinsurance tenant already has all flag keys.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.patched.map((p) => (
                  <div key={p.org} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, fontFamily: 'ui-monospace, monospace' }}>
                    <div style={{ fontWeight: 600 }}>{p.orgName} <span style={{ color: '#94a3b8' }}>({p.org.slice(-8)})</span></div>
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                      {Object.entries(p.changed).map(([k, v]) => `${k.replace('features.', '')} = ${v}`).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
      });
      loadTenants();
    } catch (err) {
      setOpsResult({
        title: 'Backfill failed',
        kind: 'err',
        body: <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div>,
      });
    } finally {
      setOpsBusy(null);
    }
  };

  // ─── Seed Atrea Demo — reinsurance-flagged demo tenant ─────────
  // Fires the generic /platform/tenants/seed-demo-tenant endpoint with an
  // Atrea-shaped payload. All tenant vocab (LOBs, currencies, branding)
  // lives on the payload — the endpoint has zero Atrea-specific logic.
  // Re-running rotates all passwords.
  const runSeedAtreaDemo = async () => {
    if (!window.confirm(
      'Seed the "Atrea Demo" reinsurance tenant on the DEFAULT (shared) cluster.\n\n' +
      'This is idempotent for USERS + TENANT CONFIG. Passwords are re-generated on every run.\n' +
      'A NEW sample deal is created every time — click twice = two deals.\n\n' +
      'What it does:\n' +
      '  • Create/update 3 users (systemAdmin, underwriter, broker)\n' +
      '  • Create/update the TenantConfig with all 5 reinsurance flags ON\n' +
      '  • Config: USD base + QAR + AED; LOBs = Property/Marine/Aviation/Energy/Casualty\n' +
      '  • Create one representative Property Facultative deal (Qatar Foundation cedent)\n\n' +
      'Passwords are shown ONCE in the result panel — copy them before dismissing.\n\n' +
      'Continue?'
    )) return;
    const payload = {
      slug:    'atrea-demo',
      orgName: 'Atrea Demo',
      branding: {
        companyName:  'Atrea',
        primaryColor: '#0b3d5c',
        industry:     'Reinsurance intermediary',
      },
      locale: {
        language: 'en', country: 'QA',
        currency: 'USD', currencySymbol: '$',
        dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Qatar',
      },
      additionalCurrencies: ['QAR', 'AED'],
      linesOfBusiness: ['Property', 'Marine', 'Aviation', 'Energy', 'Casualty'],
      structureTypes:  ['Facultative', 'Quota Share', 'Surplus', 'Excess of Loss'],
      features: {
        reinsuranceSubmissions:      true,
        reinsurancePlacement:        true,
        reinsuranceClosings:         true,
        reinsuranceClaimsValidation: true,
        reinsurancePortfolio:        true,
        reinsuranceEndorsements:     true,
        // Atrea deliberately DOES NOT enable premium depth or sanctions —
        // proves the "simple tenant sees unchanged engine + closings"
        // acceptance criterion for Module D.
      },
      infrastructure: { mongoConnectionName: 'default' },
      users: [
        { email: 'admin@atrea.demo',       name: 'Atrea System Admin', role: 'systemAdmin' },
        { email: 'underwriter@atrea.demo', name: 'Atrea Underwriter',  role: 'underwriter' },
        { email: 'broker@atrea.demo',      name: 'Atrea Broker',       role: 'broker'      },
      ],
      sampleDeal: {
        cedantName:      'Qatar General Insurance & Reinsurance Company',
        insuredName:     'Qatar Foundation',
        lineOfBusiness:  'Property',
        subclass:        'All Risks',
        geography:       'Qatar',
        structureType:   'Facultative',
        sumInsured:      500000000,
        premium:         2682388,
        currency:        'QAR',
        periodStart:     '2026-01-01',
        periodEnd:       '2026-12-31',
        status:          'bound',
        ourName:         'Atrea',
        ourShare:        0.75,
        ourPremium:      20117.91,
        cedingCommission: 22.5,
        brokerage:       0,
        terms:           'Property All Risks facultative placement. Illustrative demo values patterned on public market examples — not real client data.',
        notes:           'Seeded via Platform Tenants — used for reinsurance module demos.',
      },
    };
    await seedDemoTenant({ payload, busyKey: 'atrea', failTitle: 'Atrea Demo seed failed' });
  };

  // ─── Seed Alpine Re Demo — proves tenant-agnosticism ─────────
  // Same endpoint, same code, same modules — different tenant config
  // shape. Alpine Re is deliberately DIFFERENT from Atrea across every
  // configurable axis so a side-by-side demo shows the platform is
  // not baking anything Atrea-specific into the reinsurance module:
  //   · Different base currency (CHF vs USD)
  //   · Different additional currencies (EUR/GBP/USD vs QAR/AED)
  //   · Different LOBs (Aviation/Marine Cargo/Casualty/Financial Lines
  //     vs Property/Marine/Aviation/Energy/Casualty)
  //   · Different structure vocabulary (weighted to XoL + Surplus)
  //   · Different branding (alpine green #1e5f3f vs Atrea navy #0b3d5c)
  //   · Different sample deal (Aviation XoL vs Property Facultative)
  //   · Multi-currency markets (Munich EUR + Lloyd's USD + Swiss CHF)
  //     that exercise the FX service on credit-note generation
  //   · FX rates seeded for the pairs the credit notes will need
  const runSeedAlpineReDemo = async () => {
    if (!window.confirm(
      'Seed the "Alpine Re Demo" reinsurance tenant on the DEFAULT (shared) cluster.\n\n' +
      'Purpose: prove tenant-agnosticism — the SAME reinsurance module code produces\n' +
      'different-looking correct output for a tenant with different currency, LOBs,\n' +
      'branding, and market composition. Sit this next to Atrea Demo for the demo.\n\n' +
      'What it does:\n' +
      '  • Create/update 3 users (systemAdmin, underwriter, broker) @ alpine.demo\n' +
      '  • Create/update the TenantConfig with all 5 reinsurance flags ON\n' +
      '  • Config: CHF base + USD/EUR/GBP; LOBs = Aviation / Marine Cargo / Casualty / Financial Lines\n' +
      '  • Create one Aviation XoL deal with THREE reinsurers in MIXED currencies\n' +
      '    (Munich Re EUR · Lloyd\'s Syndicate 1234 USD · Swiss Re CHF)\n' +
      '  • Seed 3 FX rates so credit notes convert immediately\n\n' +
      'Passwords are shown ONCE in the result panel — copy them before dismissing.\n\n' +
      'Continue?'
    )) return;
    const payload = {
      slug:    'alpine-re-demo',
      orgName: 'Alpine Re Demo',
      branding: {
        companyName:  'Alpine Re',
        primaryColor: '#1e5f3f',
        industry:     'Reinsurance intermediary',
      },
      locale: {
        language: 'en', country: 'CH',
        currency: 'CHF', currencySymbol: 'CHF',
        dateFormat: 'DD.MM.YYYY', timezone: 'Europe/Zurich',
      },
      additionalCurrencies: ['USD', 'EUR', 'GBP'],
      linesOfBusiness: ['Aviation', 'Marine Cargo', 'Casualty', 'Financial Lines'],
      structureTypes:  ['Excess of Loss', 'Surplus', 'Facultative'],
      // Alpine Re turns on every premium-depth capability + defaults to
      // manual sanctions attestation. Atrea deliberately leaves both
      // sections empty — the side-by-side proves the tenant-driven
      // conditional rendering.
      premiumDepth: {
        minimumPremium:   true,
        instalments:      true,
        noClaimsBonus:    true,
        profitCommission: true,
        taxLines:         true,
      },
      sanctionsProvider:     'manual',
      sanctionsValidityDays: 90,
      features: {
        reinsuranceSubmissions:      true,
        reinsurancePlacement:        true,
        reinsuranceClosings:         true,
        reinsuranceClaimsValidation: true,
        reinsurancePortfolio:        true,
        reinsuranceEndorsements:     true,
        // Alpine Re enables Module D — proves the full-depth engine +
        // sanctions surfaces side-by-side against Atrea's simple config.
        reinsurancePremiumDepth:     true,
        reinsuranceSanctions:        true,
      },
      infrastructure: { mongoConnectionName: 'default' },
      users: [
        { email: 'admin@alpine.demo',       name: 'Alpine Re System Admin', role: 'systemAdmin' },
        { email: 'underwriter@alpine.demo', name: 'Alpine Re Underwriter',  role: 'underwriter' },
        { email: 'broker@alpine.demo',      name: 'Alpine Re Broker',       role: 'broker'      },
      ],
      sampleDeal: {
        cedantName:       'Zurich Cantonal Insurance AG',
        insuredName:      'SkyBridge Aviation Ltd',
        lineOfBusiness:   'Aviation',
        subclass:         'Airline Hull & Liability',
        geography:        'Switzerland (Zurich Airport hub)',
        occupation:       'Regional charter airline — 18-aircraft fleet',
        sumInsured:       250000000,
        premium:          3750000,
        currency:         'CHF',
        deductible:       'CHF 1,000,000 each and every loss — hull; CHF 5,000,000 aggregate — liability',
        periodStart:      '2026-06-01',
        periodEnd:        '2027-05-31',
        structureType:    'Excess of Loss',
        retentionPct:     40,
        cessionPct:       60,
        provisionalShare: 15,
        cedingCommission: 15,
        brokerage:        2.5,
        status:           'bound',
        reinsurers: [
          { name: "Munich Re",                currency: 'EUR', signedShare: 40, cedingCommission: 15, brokerage: 2.5, status: 'signed' },
          { name: "Lloyd's Syndicate 1234",   currency: 'USD', signedShare: 35, cedingCommission: 15, brokerage: 2.5, status: 'signed' },
          { name: "Swiss Re",                 currency: 'CHF', signedShare: 25, cedingCommission: 15, brokerage: 2.5, status: 'signed' },
        ],
        terms:           'Aviation XoL layer covering hull and passenger liability. Illustrative demo values — not real client data.',
        notes:           'Seeded via Platform Tenants — Alpine Re tenant-agnostic proof.',
      },
      // Rates chosen for the demo period. Not real market fixes.
      fxRates: [
        { base: 'CHF', quote: 'USD', rate: 1.13, effectiveDate: '2026-06-01', source: 'seed', notes: 'Illustrative rate for demo' },
        { base: 'CHF', quote: 'EUR', rate: 1.04, effectiveDate: '2026-06-01', source: 'seed', notes: 'Illustrative rate for demo' },
        { base: 'USD', quote: 'EUR', rate: 0.92, effectiveDate: '2026-06-01', source: 'seed', notes: 'Illustrative rate for demo' },
      ],
    };
    await seedDemoTenant({ payload, busyKey: 'alpine', failTitle: 'Alpine Re Demo seed failed' });
  };

  const runResetSaasGodPassword = async () => {
    if (!window.confirm(
      'Reset the SaaS God password (admin@getregure.com)?\n\n' +
      'A new random password will be generated and shown ONCE. You must copy it now and store it in a password manager — it cannot be recovered afterwards.\n\n' +
      'Any active sessions for admin@getregure.com will remain valid until they expire.\n\n' +
      'Continue?'
    )) return;
    setOpsBusy('reset');
    setOpsResult(null);
    try {
      const { data } = await axios.post(`${BASE_URL}/platform/reset-saas-god-password`, {});
      const newPw = data?.password?.value;
      setOpsResult({
        title: `New password issued for ${data.account.email}`,
        kind:  'ok',
        body: (
          <div style={{ fontSize: 12, color: C.textPrimary }}>
            <div style={{ marginBottom: 10, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontWeight: 600 }}>
              ⚠ This password will not be shown again. Copy it now.
            </div>
            <div style={{
              display: 'flex', alignItems: 'stretch', gap: 6,
              padding: 0, background: '#0f172a', color: '#f8fafc',
              border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden',
            }}>
              <div style={{
                flex: 1, padding: '10px 12px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13, letterSpacing: 0.4, wordBreak: 'break-all',
                userSelect: 'all',
              }}>
                {newPw}
              </div>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(newPw);
                    setCopyOk(true);
                    setTimeout(() => setCopyOk(false), 1800);
                  } catch { /* clipboard unavailable — user selects manually */ }
                }}
                style={{
                  background: '#334155', color: '#f8fafc', border: 'none',
                  padding: '0 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  minWidth: 76,
                }}
                title='Copy to clipboard'
              >
                {copyOk ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: C.textMuted }}>
              {data.password.hint}
            </div>
          </div>
        ),
      });
    } catch (err) {
      setOpsResult({
        title: 'Reset failed',
        kind:  'err',
        body:  <div style={{ fontSize: 12 }}>{err.response?.data?.message || err.message}</div>,
      });
    } finally {
      setOpsBusy(null);
    }
  };

  return (
    <AppLayout>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
       <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div>
            <h1 style={{ fontSize: 24, color: C.textPrimary, margin: 0, fontWeight: 700 }}>{t('screens.platformTenants.title')}</h1>
            <p style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>{t('screens.platformTenants.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={runMigrateKeys}
              disabled={opsBusy !== null}
              title='Re-key TenantConfig docs to match req.user.org — supports dry-run.'
              style={{
                padding: '10px 14px', background: '#fff', color: C.textPrimary,
                border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'migrate' ? 0.5 : 1,
              }}
            >
              {opsBusy === 'migrate' ? t('screens.platformTenants.migrating') : t('screens.platformTenants.migrateKeys')}
            </button>
            {isPlatformAdminDeploy && (
              <>
                <button
                  onClick={runSeedSaasGod}
                  disabled={opsBusy !== null}
                  title='Create or repair admin@getregure.com and its all-features-on TenantConfig.'
                  style={{
                    padding: '10px 14px', background: '#fff', color: C.textPrimary,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'seed' ? 0.5 : 1,
                  }}
                >
                  {opsBusy === 'seed' ? t('screens.platformTenants.seeding') : t('screens.platformTenants.seedSaasGod')}
                </button>
                <button
                  onClick={runResetSaasGodPassword}
                  disabled={opsBusy !== null}
                  title='Issue a new random password for admin@getregure.com — shown once.'
                  style={{
                    padding: '10px 14px', background: '#fff', color: C.textPrimary,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'reset' ? 0.5 : 1,
                  }}
                >
                  {opsBusy === 'reset' ? 'Resetting…' : 'Reset SaaS God Password'}
                </button>
                <button
                  onClick={runSeedMinnesota}
                  disabled={opsBusy !== null}
                  title='Seed the Minnesota River trial tenant on the shared default cluster.'
                  style={{
                    padding: '10px 14px', background: '#fff', color: C.textPrimary,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'mn-seed' ? 0.5 : 1,
                  }}
                >
                  {opsBusy === 'mn-seed' ? 'Seeding Minnesota…' : 'Seed Minnesota Trial'}
                </button>
                <button
                  onClick={runSeedAtreaDemo}
                  disabled={opsBusy !== null}
                  title='Seed the Atrea Demo reinsurance tenant on the shared default cluster. Rotates passwords on every run.'
                  style={{
                    padding: '10px 14px', background: '#fff', color: C.textPrimary,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'atrea' ? 0.5 : 1,
                  }}
                >
                  {opsBusy === 'atrea' ? 'Seeding Atrea Demo…' : 'Seed Atrea Demo'}
                </button>
                <button
                  onClick={runSeedAlpineReDemo}
                  disabled={opsBusy !== null}
                  title='Seed the Alpine Re Demo tenant — same code, different config (CHF base, aviation XoL, mixed-currency markets, seeded FX rates). Proof of tenant-agnosticism.'
                  style={{
                    padding: '10px 14px', background: '#fff', color: C.textPrimary,
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'alpine' ? 0.5 : 1,
                  }}
                >
                  {opsBusy === 'alpine' ? 'Seeding Alpine Re…' : 'Seed Alpine Re Demo'}
                </button>
              </>
            )}
            <button
              onClick={runBackfillReinsuranceFlags}
              disabled={opsBusy !== null}
              title='Additive: ensures every reinsurance-enabled tenant has all 8 sub-flags. Fixes tenants seeded before Endorsements / Premium Depth / Sanctions existed. Idempotent.'
              style={{
                padding: '10px 14px', background: '#fff', color: C.textPrimary,
                border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: opsBusy ? 'wait' : 'pointer', opacity: opsBusy && opsBusy !== 'backfill' ? 0.5 : 1,
              }}
            >
              {opsBusy === 'backfill' ? 'Backfilling flags…' : 'Backfill Reinsurance Flags'}
            </button>
            <button
              onClick={() => setShowNew(true)}
              style={{
                padding: '10px 18px', background: C.burgundy, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('screens.platformTenants.newTenantConfig')}
            </button>
          </div>
        </div>

        {opsResult && (
          <div style={{
            marginTop: 16,
            background: opsResult.kind === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${opsResult.kind === 'ok' ? '#86efac' : '#fca5a5'}`,
            borderRadius: 10, padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: opsResult.kind === 'ok' ? '#047857' : '#991b1b' }}>
                {opsResult.title}
              </div>
              <button
                onClick={() => setOpsResult(null)}
                style={{ background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
                aria-label={t('screens.platformTenants.dismiss')}
              >
                ×
              </button>
            </div>
            {opsResult.body}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginTop: 20, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Card grid */}
        {loading && (
          <div style={{ marginTop: 28, padding: 28, textAlign: 'center', color: C.textMuted, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12 }}>
            {t('screens.platformTenants.loading')}
          </div>
        )}

        {!loading && tenants.length === 0 && (
          <div style={{ marginTop: 28, padding: 40, textAlign: 'center', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 6 }}>
              {t('screens.platformTenants.emptyTitle')}
            </div>
            <div style={{ fontSize: 13, color: C.textMuted, maxWidth: 540, margin: '0 auto' }}>
              {t('screens.platformTenants.emptyDesc')}
            </div>
          </div>
        )}

        {!loading && tenants.length > 0 && (
          <div style={{
            marginTop: 24,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 14,
          }}>
            {tenants.map(tn => {
              const c = countEnabled(tn.features);
              const region = tn.infrastructure?.s3?.region || tn.infrastructure?.mongoConnectionName || '—';
              const locale = tn.locale ? `${tn.locale.language}-${tn.locale.country} · ${tn.locale.currency}` : '—';
              const pct = c.total > 0 ? Math.round((c.enabled / c.total) * 100) : 0;
              return (
                <div
                  key={String(tn._id || tn.org)}
                  onClick={() => history.push(`/platform/tenants/${tn.org}`)}
                  style={{
                    background: '#fff',
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: 14,
                    cursor: 'pointer',
                    transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.12s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = C.burgundy;
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(128,0,32,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Top row: name + status */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tn.orgName || t('screens.platformTenants.unnamed')}
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'monospace', marginTop: 2 }}>
                        {String(tn.org).slice(-12)}
                      </div>
                    </div>
                    <Badge active={tn.isActive !== false} />
                  </div>

                  {/* Feature progress */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
                      <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{t('screens.platformTenants.features')}</span>
                      <span style={{ fontWeight: 600, color: C.textPrimary }}>{c.enabled} / {c.total}</span>
                    </div>
                    <div style={{ height: 4, background: C.bgCard, borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: C.burgundy, transition: 'width 0.3s' }} />
                    </div>
                  </div>

                  {/* Meta rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                    <MetaRow k={t('screens.platformTenants.region')} v={region} />
                    <MetaRow k={t('screens.platformTenants.locale')} v={locale} />
                    <MetaRow k={t('screens.platformTenants.updated')} v={formatDate(tn.updatedAt)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

       </div>

      {showNew && (
        <NewTenantModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadTenants(); }} />
      )}
    </AppLayout>
  );
};

function MetaRow({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <span style={{ color: C.textMuted, textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>{k}</span>
      <span style={{ color: C.textPrimary, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>{v}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// "+ New Tenant Config" modal — simple inline modal, no shared component
// ──────────────────────────────────────────────────────────────
function NewTenantModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [candidates,  setCandidates]  = useState([]);
  const [candLoading, setCandLoading] = useState(true);
  const [pickedOrgId, setPickedOrgId] = useState('');
  const [rawOrgId,    setRawOrgId]    = useState('');
  const [orgName,     setOrgName]     = useState('');
  const [presetKey,   setPresetKey]   = useState('agencyCompareReview');
  const [localeKey,   setLocaleKey]   = useState('US');
  const [mongoConn,   setMongoConn]   = useState('default');
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load candidate orgs from /platform/tenants/discover.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/platform/tenants/discover`);
        const list = (data?.data || []).filter(c => !c.hasConfig);
        setCandidates(list);
      } catch {
        setCandidates([]);
      } finally {
        setCandLoading(false);
      }
    })();
  }, []);

  // When picking from the dropdown, pre-fill orgName.
  const onPickChange = (id) => {
    setPickedOrgId(id);
    const c = candidates.find(x => String(x.orgId) === id);
    if (c) setOrgName(c.name || c.email || '');
  };

  const submit = async () => {
    const orgId = (pickedOrgId || rawOrgId).trim();
    if (!orgId) { setSubmitError(t('screens.platformTenants.noOrgError')); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${BASE_URL}/platform/tenants`, {
        org:      orgId,
        orgName:  orgName.trim() || undefined,
        features: FEATURE_PRESETS[presetKey].features,
        infrastructure: { mongoConnectionName: mongoConn.trim() || 'default', s3: { bucket: null, region: null } },
        locale:   LOCALE_PRESETS[localeKey],
        isActive: true,
      });
      onCreated();
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBody} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: C.textPrimary, margin: 0, marginBottom: 4 }}>{t('screens.platformTenants.newModalTitle')}</h2>
        <p style={{ fontSize: 12, color: C.textMuted, marginTop: 0, marginBottom: 20 }}>
          {t('screens.platformTenants.newModalDesc')}
        </p>

        <Field label={t('screens.platformTenants.unconfiguredTenants')}>
          <select value={pickedOrgId} onChange={e => onPickChange(e.target.value)} style={input} disabled={candLoading}>
            <option value="">
              {candLoading
                ? t('screens.platformTenants.loadingCandidates')
                : candidates.length === 0
                  ? t('screens.platformTenants.noneAvailable')
                  : t('screens.platformTenants.pickTenant')}
            </option>
            {candidates.map(c => (
              <option key={String(c.orgId)} value={String(c.orgId)}>
                {c.name || '(no name)'} — {c.email || '(no email)'} [{c.role}] ({c.memberCount || 1} users)
              </option>
            ))}
          </select>
        </Field>

        <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'center', margin: '4px 0 8px' }}>{t('screens.platformTenants.or')}</div>

        <Field label={t('screens.platformTenants.rawUserId')}>
          <input value={rawOrgId} onChange={e => { setRawOrgId(e.target.value); setPickedOrgId(''); }}
            placeholder="69cc34babd80e7f36ff4bcda"
            style={input} />
        </Field>

        <Field label={t('screens.platformTenants.displayName')}>
          <input value={orgName} onChange={e => setOrgName(e.target.value)}
            placeholder="ARGOS Group"
            style={input} />
        </Field>

        <Field label={t('screens.platformTenants.featurePreset')}>
          <select value={presetKey} onChange={e => setPresetKey(e.target.value)} style={input}>
            {Object.entries(FEATURE_PRESETS).map(([k, v]) => (
              <option key={k} value={k}>{v.label} — {v.description}</option>
            ))}
          </select>
        </Field>

        <Field label={t('screens.platformTenants.localeLabel')}>
          <select value={localeKey} onChange={e => setLocaleKey(e.target.value)} style={input}>
            <option value="US">{t('screens.platformTenants.localeUS')}</option>
            <option value="CH">{t('screens.platformTenants.localeCH')}</option>
          </select>
        </Field>

        <Field label={t('screens.platformTenants.mongoConn')}>
          <input value={mongoConn} onChange={e => setMongoConn(e.target.value)}
            placeholder="default | argosStaging | minnesotaStaging"
            style={input} />
        </Field>

        {submitError && (
          <div style={{ background: '#fef2f2', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: 12, marginTop: 12 }}>
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={btnSecondary}>{t('screens.platformTenants.cancel')}</button>
          <button onClick={submit} disabled={submitting} style={btnPrimary}>
            {submitting ? t('screens.platformTenants.creating') : t('screens.platformTenants.createConfig')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const input = {
  width: '100%', padding: '9px 11px', fontSize: 13, color: C.textPrimary,
  border: `1px solid ${C.border}`, borderRadius: 7, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#fff',
};

const btnPrimary = {
  padding: '9px 18px', background: C.burgundy, color: '#fff',
  border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '9px 18px', background: '#fff', color: C.textPrimary,
  border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBody = {
  background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 540,
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
};

export default PlatformTenants;
